import { startCase } from "lodash";
import { t } from "@/i18n-js/instance";
import { MEMBER_FIELDS } from "../components/common/copilot/PreviewCards/MemberCard";
import { PROFILE_FIELDS } from "../components/common/copilot/PreviewCards/ProfileFieldCard";
import { SPACE_FIELDS } from "../components/common/copilot/PreviewCards/SpaceCard";
import type { ChatButton } from "../types/ChatButton";
import type { ChatCardProps } from "../types/ChatCard";
import type { Attachment } from "../types/attachment";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { convertHtmlToText } from "./htmlToText";
import { extractCards } from "./messageUtils";
import { getFormattedValue } from "./previewValueFormatterUtils";

const formatAttachments = (attachments: Attachment[]): string => {
  if (!attachments || attachments.length === 0) return "";
  const fileList = attachments.map(a => `- ${a.filename}`).join("\n");
  return `[Attachments]\n${fileList}`;
};

const formatButtons = (
  buttons: ChatButton[],
  selectedButtonEvent: string | null,
): string => {
  if (!buttons || buttons.length === 0) return "";
  return `[Options]\n${buttons
    .map(b => {
      let text = b.label;
      if (b.sublabel) text += ` (${b.sublabel})`;
      if (selectedButtonEvent === b.event) text += " [SELECTED]";
      return `- ${text}`;
    })
    .join("\n")}`;
};

const formatPreviewCard = (content: {
  title?: string;
  url?: string;
  resource: string;
  params: Record<string, string | string[] | boolean>;
}): string => {
  const { resource, params, url, title } = content;
  const result: string[] = [];

  // Add title at the top (matches card title)
  if (title) {
    result.push(title);
  }

  const FIELD_CONFIGS: Record<
    string,
    Record<string, { label: string; type: string }>
  > = {
    space: SPACE_FIELDS,
    profile_field: PROFILE_FIELDS,
    member: MEMBER_FIELDS,
    member_profile: MEMBER_FIELDS,
  };

  const fieldConfig = FIELD_CONFIGS[resource];

  // Add fields in the middle
  if (fieldConfig) {
    const paramLines = Object.entries(fieldConfig)
      .map(([key, keyProps]) => {
        const hasValueInParams = key in params && params[key] != null;
        const value = hasValueInParams
          ? params[key]
          : (keyProps as any).defaultValue;

        if (value == null) return null;

        return `• ${keyProps.label}: ${getFormattedValue(value, keyProps.type)}`;
      })
      .filter(Boolean)
      .join("\n");

    if (paramLines) {
      result.push(paramLines);
    }
  } else {
    const paramLines = Object.entries(params)
      .filter(([, value]) => value != null)
      .map(([key, value]) => {
        const label = startCase(key);
        let formattedValue = String(value);
        if (typeof value === "boolean") {
          formattedValue = value ? t("yes") : t("no");
        } else if (Array.isArray(value)) {
          formattedValue = value.join(", ");
        }
        return `• ${label}: ${formattedValue}`;
      })
      .join("\n");

    if (paramLines) {
      result.push(paramLines);
    }
  }

  // Add link at the bottom (matches card button)
  if (url) {
    const linkText = title ? `Link: [${title}](${url})` : `Link: ${url}`;
    result.push(linkText);
  }

  return result.filter(Boolean).join("\n\n");
};

const formatConfirmationCard = (content: {
  title?: string;
  params: Record<string, unknown>;
}): string => {
  const { title, params } = content;
  const result: string[] = [];

  if (title) {
    result.push(title);
  }

  const paramLines = Object.entries(params)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .map(([key, value]) => {
      const label = startCase(key);
      return `• ${label}: ${String(value)}`;
    })
    .join("\n");

  if (paramLines) {
    result.push(paramLines);
  }

  return result.filter(Boolean).join("\n");
};

const formatListingCard = (
  content: { resources: Array<Record<string, unknown>> },
  cardType: string,
): string => {
  const { resources } = content;

  if (!resources || resources.length === 0) {
    return "";
  }

  // Get the title based on card type (matches ListingCard titles)
  const LISTING_CARD_TITLES: Record<string, string> = {
    member_listing_card: t("help_widget.copilot.chat_card.members_matching"),
    invitation_link_listing_card: t(
      "help_widget.copilot.chat_card.invitation_links",
    ),
    member_tag_listing_card: t("help_widget.copilot.chat_card.member_tags"),
    profile_field_listing_card: t(
      "help_widget.copilot.chat_card.profile_fields",
    ),
    space_group_listing_card: t("help_widget.copilot.chat_card.space_groups"),
    space_listing_card: t("help_widget.copilot.chat_card.spaces"),
    access_group_listing_card: t("help_widget.copilot.chat_card.access_groups"),
    community_segment_listing_card: t(
      "help_widget.copilot.chat_card.community_segments",
    ),
  };

  const title = LISTING_CARD_TITLES[cardType] || "";
  const result: string[] = [];

  if (title) {
    result.push(title);
  }

  // Format each resource matching LabelWithDetail component
  const formatResource = (resource: Record<string, unknown>): string => {
    const label =
      (resource.name as string) ||
      (resource.label as string) ||
      (resource.title as string) ||
      "";

    const details: string[] = [];

    // Email for members
    if (resource.email) {
      details.push(resource.email as string);
    }

    // Status for invitation links
    if (resource.status) {
      details.push(resource.status as string);
    }

    // Space type for spaces
    if (resource.space_type) {
      const spaceNameLabel = t(
        `settings.spaces.space_types.${resource.space_type as string}`,
      ).toLowerCase();
      const typeLabel = t("help_widget.copilot.chat_card.space_types", {
        space_type: spaceNameLabel,
      });
      details.push(typeLabel);
    }

    // Field type for profile fields
    if (resource.field_type) {
      details.push(resource.field_type as string);
    }

    // Visibility for tags
    if (resource.is_public !== undefined) {
      const isPublic = resource.is_public as boolean;
      const statusKey = isPublic ? "public" : "private";
      const translationKey = `help_widget.copilot.chat_card.tag_visibility.${statusKey}`;
      const translated = t(translationKey);
      const visibilityLabel =
        translated === translationKey ? statusKey : translated;
      details.push(visibilityLabel);
    }

    // Format as "label (detail)" matching LabelWithDetail component
    if (details.length > 0) {
      return `${label} (${details.join(", ")})`;
    }

    return label;
  };

  const resourceLines = resources
    .map((resource, index) => `${index + 1}. ${formatResource(resource)}`)
    .join("\n");
  if (resourceLines) {
    result.push(resourceLines);
  }

  return result.filter(Boolean).join("\n\n");
};

const formatLinkCard = (content: { title?: string; url?: string }): string => {
  const { title, url } = content;
  if (url) return title ? `Link: [${title}](${url})` : `Link: ${url}`;
  return title || "";
};

export const formatCardAsText = (card: ChatCardProps): string => {
  const { type, content } = card;

  switch (type) {
    case "preview_card":
      return formatPreviewCard(content as any);

    case "confirmation_card":
      return formatConfirmationCard(content as any);

    case "link_card":
      return formatLinkCard(content as any);

    case "member_listing_card":
    case "invitation_link_listing_card":
    case "member_tag_listing_card":
    case "profile_field_listing_card":
    case "space_group_listing_card":
    case "space_listing_card":
    case "access_group_listing_card":
    case "community_segment_listing_card":
      return formatListingCard(content as any, type);

    default:
      return "";
  }
};

const formatCards = (cards: ChatCardProps[]): string => {
  if (!cards || cards.length === 0) return "";

  return cards.map(formatCardAsText).filter(Boolean).join("\n\n");
};

export const formatMessageAsText = (message: HelpWidgetMessage): string => {
  const contentParts = [convertHtmlToText(message.content)];

  if (message.attachments && message.attachments.length > 0) {
    contentParts.push(formatAttachments(message.attachments));
  }

  if (message.buttons && message.buttons.length > 0) {
    contentParts.push(formatButtons(message.buttons, message.selected_button));
  }

  const extractedCards = extractCards(message) as ChatCardProps[];
  if (extractedCards && extractedCards.length > 0) {
    contentParts.push(formatCards(extractedCards));
  }

  return contentParts.filter(Boolean).join("\n\n");
};
